import React, { useEffect, useState, useRef } from "react";
import { fetchProductVersions, restoreVersion } from "src/redux/versioning/versioningThunk";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import SpinnerLoader from "../components/Spinner";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { ImWarning } from "react-icons/im";
import { MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { addProduct } from "src/redux/products/productThunk";
import { EDIT } from "src/helper/constants/constants";

const VersionTable = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const _state = useSelector((state) => state.versioning, shallowEqual);
  const inputRef = useRef(null);

  const [show, setShow] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [differences, setDifferences] = useState([]);
  const [comparing, setComparing] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const productCode = props.gtin ? props.gtin : props._2an;

  const isLoading = _state.actionsLoading;
  const isCompareLoading = comparing;


  // Compare versions whenever selectedVersion changes
  useEffect(() => {
    if (selectedVersion && currentVersion) {
      setComparing(true);

      try {
        // Flatten both objects for comparison
        const flatCurrent = flattenObject(currentVersion.data);
        const flatSelected = flattenObject(selectedVersion.data);

        // Get all unique keys
        const allKeys = new Set([...Object.keys(flatCurrent), ...Object.keys(flatSelected)]);

        const diffs = [];

        allKeys.forEach(key => {
          const currentValue = flatCurrent[key];
          const selectedValue = flatSelected[key];

          // Check if values are different
          if (JSON.stringify(currentValue) !== JSON.stringify(selectedValue)) {
            diffs.push({
              key: key.split('.').pop(), // Get the last part of the path
              path: key,
              selectedValue: selectedValue === undefined ? '(empty)' : formatValue(selectedValue),
              currentValue: currentValue === undefined ? '(empty)' : formatValue(currentValue),
            });
          }
        });

        setDifferences(diffs);
      } catch (error) {
        console.error('Error comparing versions:', error);
      } finally {
        setComparing(false);
      }
    }
  }, [selectedVersion, currentVersion]);

  // Load initial data
  useEffect(() => {
    if (props.gtin || props._2an) {
      dispatch(fetchProductVersions(productCode));

      if (props.mode !== "product") {
        inputRef.current.value = productCode;
      }
    }
  }, []);

  // Update current version when versions data changes
  useEffect(() => {
    if (_state.productVersions && _state.productVersions.length > 0) {
      const sortedVersions = [..._state.productVersions].sort(
        (a, b) => new Date(b.timeCreated) - new Date(a.timeCreated)
      );

      // Always update current version to the most recent
      if (sortedVersions.length > 0) {
        setCurrentVersion(sortedVersions[0]);
        // Reset selected version if it's now the current version
        if (selectedVersion && selectedVersion.generation === sortedVersions[0].generation) {
          setSelectedVersion(null);
          setDifferences([]);
        }
      }
    }
  }, [_state.productVersions]);



  // Sort versions by timeCreated (newest first)
  const sortedVersions = _state.productVersions
    ? [..._state.productVersions].sort((a, b) => new Date(b.timeCreated) - new Date(a.timeCreated))
    : [];

  // Handle cancel and close button click in modal
  const handleClose = () => {
    setShow(false);
    setVersionToRestore();
  };

  // Handle OK button click in modal
  const handleOkClick = async () => {
    if (versionToRestore) {
      try {
        setIsRestoring(true);
        await dispatch(
          addProduct({ productData: versionToRestore?.data, formType: EDIT })
        ).unwrap();
        setIsRestoring(false);
        setShow(false);
        dispatch(fetchProductVersions(productCode));

      } catch (error) {
        console.log(error);
        setIsRestoring(false);
      }
    }
  };

  // Handle compare button click
  const handleCompare = (version) => {
    setSelectedVersion(version);
  };

  // Get versions of wanted GTIN
  const getVersions = () => {
    if (inputRef.current.value) {
      dispatch(fetchProductVersions(inputRef.current.value));
    }
  };

  // Handle Restore button click in versions list
  const handleRestoreButtonClick = (version) => {
    setVersionToRestore(version);
    setShow(true);
  };

  // Flatten object for comparison
  const flattenObject = (obj, prefix = '') => {
    const flattened = {};

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;

        // Skip metadata fields
        if (['generation', 'size', 'isLive', 'timeCreated', 'updated'].includes(key)) {
          continue;
        }

        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          Object.assign(flattened, flattenObject(value, newKey));
        } else {
          flattened[newKey] = value;
        }
      }
    }

    return flattened;
  };

  const formatValue = (value) => {
    if (value === null) return '(empty)';
    if (value === undefined) return '(empty)';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'string' && value.trim() === '') return '(empty)';
    return String(value);
  };

  const formatDateTime = (dateString) => {
    try {
      return moment(dateString).format('YYYY-MM-DD HH:mm:ss');
    } catch {
      return dateString;
    }
  };



  return (
    <div style={{ height: '100%' }}>
      {props.mode !== "product" ? (
        <div className="row mt-2 mb-4">
          <div className="col-8">
            <form className="form-inline">
              <input
                className="form-control me-2"
                type="text"
                id="gtin"
                placeholder={t("_GTIN")}
                ref={inputRef}
              />
              <button
                type="submit"
                className="btn btn-primary"
                onClick={() => getVersions()}
              >
                {t("Search")}
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <SpinnerLoader />
            <p style={{ marginTop: '1rem', color: '#6c757d' }}>{t("Loading version history")}...</p>
          </div>
        </div>
      ) : !_state.productVersions || _state.productVersions.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#6c757d', fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              {t("No version history available")}
            </p>
          </div>
        </div>
      ) : (
        <div style={{
          height: '100%',
          display: 'flex',
          gap: '1rem',
          padding: '1rem',
          flexDirection: window.innerWidth < 992 ? 'column' : 'row'
        }}>
          {/* Left Panel - Version Timeline */}
          <div style={{
            width: window.innerWidth < 992 ? '100%' : '240px',
            borderRight: window.innerWidth < 992 ? 'none' : '1px solid #dee2e6',
            paddingRight: window.innerWidth < 992 ? '0' : '1rem'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              marginBottom: '1rem',
              color: '#3a0b61'
            }}>
              {t("Versioning")}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {sortedVersions.map((version, index) => {
                const isCurrentVersion = index === 0;
                const isSelected = selectedVersion?.generation === version.generation;

                return (
                  <div
                    key={version.generation}
                    style={{
                      padding: '0.75rem',
                      border: isSelected ? '2px solid #2b0648' : '1px solid #dee2e6',
                      borderRadius: '0.5rem',
                      backgroundColor: isSelected ? '#fce7f3' : 'white',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.875rem',
                      color: '#6c757d',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ marginRight: '0.375rem' }}>🕐</span>
                      {formatDateTime(version.timeCreated)}
                    </div>

                    {isCurrentVersion ? (
                      <span style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#198754',
                        backgroundColor: '#d1e7dd',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        display: 'inline-block'
                      }}>
                        {t("Current version")}
                      </span>
                    ) : (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleCompare(version)}
                          className="btn btn-sm"
                          style={{
                            backgroundColor: 'rgb(0, 123, 255)',
                            color: 'white',
                            border: 'none',
                            fontSize: '0.875rem',
                            padding: '0.375rem 0.75rem',
                            cursor: 'pointer',
                            borderRadius: '0.25rem'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = 'rgb(0, 105, 217)'}
                          onMouseOut={(e) => e.target.style.backgroundColor = 'rgb(0, 123, 255)'}
                        >
                          {t("Compare")}
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{
                            fontSize: '0.875rem',
                            padding: '0.375rem 0.75rem',
                            cursor: 'pointer',
                            backgroundColor: 'rgb(220, 53, 69)',
                          }}
                          onClick={() => handleRestoreButtonClick(version)}
                        >
                          {t("Restore")}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel - Differences */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {!selectedVersion ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#6c757d'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>ℹ️</div>
                  <p style={{ fontSize: '1.125rem', fontWeight: '500' }}>
                    {t("Select a version to compare")}
                  </p>
                  <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    {t("Click")} "{t("Compare")}" {t("on any previous version to see changes")}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  color: '#212529',
                  textAlign: 'center'
                }}>
                  {t("Differences between selected and current version")}
                </h3>

                {isCompareLoading ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '12rem'
                  }}>
                    <SpinnerLoader />
                  </div>
                ) : differences.length === 0 ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '12rem',
                    color: '#6c757d'
                  }}>
                    <p>{t("No differences found")}</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      className="table table-bordered"
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: 'white'
                      }}
                    >
                      <thead style={{ backgroundColor: '#f8f9fa' }}>
                        <tr>
                          <th style={{
                            border: '1px solid #dee2e6',
                            padding: '0.75rem 1rem',
                            textAlign: 'left',
                            fontWeight: '600'
                          }}>
                            {t("Key")}
                          </th>
                          <th style={{
                            border: '1px solid #dee2e6',
                            padding: '0.75rem 1rem',
                            textAlign: 'left',
                            fontWeight: '600'
                          }}>
                            {t("Path")}
                          </th>
                          <th style={{
                            border: '1px solid #dee2e6',
                            padding: '0.75rem 1rem',
                            textAlign: 'left',
                            fontWeight: '600'
                          }}>
                            {t("Value in selected version")}
                          </th>
                          <th style={{
                            border: '1px solid #dee2e6',
                            padding: '0.75rem 1rem',
                            textAlign: 'left',
                            fontWeight: '600'
                          }}>
                            {t("Value in current version")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {differences.map((diff, index) => (
                          <tr key={index} style={{
                            transition: 'background-color 0.2s',
                          }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                          >
                            <td style={{
                              border: '1px solid #dee2e6',
                              padding: '0.75rem 1rem',
                              fontWeight: '500'
                            }}>
                              {diff.key}
                            </td>
                            <td style={{
                              border: '1px solid #dee2e6',
                              padding: '0.75rem 1rem',
                              fontSize: '0.875rem',
                              color: '#6c757d'
                            }}>
                              {diff.path}
                            </td>
                            <td style={{
                              border: '1px solid #dee2e6',
                              padding: '0.75rem 1rem'
                            }}>
                              <span style={{
                                color: diff.selectedValue === '(empty)' ? '#dc3545' : '#212529',
                                fontStyle: diff.selectedValue === '(empty)' ? 'italic' : 'normal'
                              }}>
                                {diff.selectedValue}
                              </span>
                            </td>
                            <td style={{
                              border: '1px solid #dee2e6',
                              padding: '0.75rem 1rem'
                            }}>
                              <span style={{
                                color: diff.currentValue === '(empty)' ? '#dc3545' : '#198754',
                                fontStyle: diff.currentValue === '(empty)' ? 'italic' : 'normal',
                                fontWeight: diff.currentValue !== '(empty)' ? '500' : 'normal'
                              }}>
                                {diff.currentValue}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <Modal size="md" show={show} onHide={handleClose} centered={true}>
        <Modal.Header>
          <span
            className="close-text"
            onClick={handleClose}
            style={{
              marginLeft: 'auto',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}
          >
            <MdClose
              style={{ marginBottom: 2, color: "black" }}
              fontSize={24}
              fontWeight={"bold"}
            />
            {t("Close")}
          </span>
        </Modal.Header>
        <Modal.Body
          style={{
            display: "block",
            padding: 50,
            textAlign: "center",
            color: "#131e27",
            opacity: 1,
            fontWeight: "bold",
            fontSize: 18,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <ImWarning
              style={{
                width: 40,
                height: 40,
                textAlign: "center",
                alignSelf: "center",
                color: "red",
                marginBottom: "20px",
              }}
            />
            <span>{t("Do you really want to restore this version")}</span>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: "center" }}>
          <Button variant="secondary" type="button" onClick={handleClose} disabled={isRestoring}>
            {t("Cancel")}
          </Button>
          <Button variant="danger" type="button" onClick={handleOkClick} disabled={isRestoring}>
            {isRestoring ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                {t("Restoring")}...
              </span>
            ) : (
              t("Ok")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VersionTable;
